/**
 * Remove dependencies for useEffect on setPhysicalStore.
 * To solve the infinite loop issue for PDP page.
 */

import { HdmNotAvailable } from '@/components/blocks/HdmNotAvailable';
import { ProductDetailsAddToCart } from '@/components/blocks/ProductDetails/AddToCart';
import { ProductDetailsDisplay } from '@/components/blocks/ProductDetails/Display';
import { ProductDetailsPrice } from '@/components/blocks/ProductDetails/Price';
import { ProductGalleryTabs } from '@/components/blocks/ProductDetails/ProductGalleryTabs';
import { VideoGalleryProvider } from '@/components/blocks/ProductDetails/context/VideoGalleryContext';
import { ProductAbout } from '@/components/blocks/ProductDetails/parts/ProductAbout';
import { ProductAvailability } from '@/components/blocks/ProductDetails/parts/ProductAvailability';
import { productDetailsContainerSX } from '@/components/blocks/ProductDetails/styles/container';
import { PromotionCard } from '@/components/blocks/PromotionCard';
import { EMarketingSpot } from '@/components/content/EMarketingSpot';
import { KDP_CROSS_SELL_ESPOT, KDP_UX_COMPONENT_ESPOT } from '@/components/content/Kit/constants';
import { KitTable } from '@/components/content/Kit/parts/Table';
import { kitSX } from '@/components/content/Kit/styles/style';
import { PDP_NOT_AVAILABLE_SKU_ESPOT } from '@/components/content/ProductDetails/constants';
import { ProductDetailsAttributes } from '@/components/content/ProductDetails/parts/Attributes';
import { DeliveryMethodSelector } from '@/components/content/ProductDetails/parts/DeliveryMethodSelector';
import { PaperCard } from '@/components/content/ProductDetails/parts/PaperCards';
import { ProductDetailsSkeleton } from '@/components/skeleton/components/elements/PDP';
import { useDefaultStore } from '@/data/Content/DefaultStore';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useLocalization } from '@/data/Localization';
import { ContentProvider } from '@/data/context/content';
import { ID } from '@/data/types/Basic';
import { Box, Paper, Typography } from '@mui/material';
import { useAdobePDPViews } from 'presentation/events/hooks/adobe/useAdobePDPViews';
import { usePDPTrackLoadEvents } from 'presentation/events/hooks/usePDPTrackLoadEvents';
import { FC } from 'react';

export const Kit: FC<{
	id: ID;
}> = ({ id }) => {
	const { uniqueId: physicalStoreId } = useDefaultStore();
	const productDetails = useProductDetails({
		partNumber: id.toString(),
		physicalStoreIds: [physicalStoreId ?? ''],
	});
	const { product, selection, isBuyable, isLoading } = productDetails;
	const { KitTittle } = useLocalization('HDMProductDetail');

	usePDPTrackLoadEvents({
		product: selection?.sku ?? product,
	});
	useAdobePDPViews(product?.partNumber);

	if (isLoading) {
		return <ProductDetailsSkeleton />;
	}

	if (!selection || !product?.partNumber || !isBuyable) {
		return (
			<HdmNotAvailable
				emsName={PDP_NOT_AVAILABLE_SKU_ESPOT}
				sx={{
					width: '100%',
				}}
			/>
		);
	}
	return (
		<ContentProvider value={productDetails}>
			<Box sx={kitSX.container}>
				<Box sx={kitSX.leftContainer}>
					<Paper sx={kitSX.gallery}>
						<VideoGalleryProvider>
							<ProductGalleryTabs sku={product.partNumber} />
						</VideoGalleryProvider>
					</Paper>
				</Box>
				<Box sx={kitSX.rightContainer}>
					<PaperCard elevation={1} sx={kitSX.paperName}>
						<ProductDetailsDisplay />
					</PaperCard>
					<PaperCard elevation={1} sx={kitSX.paperPrice}>
						<ProductDetailsPrice variant="xl" />
						<PromotionCard
							id={selection?.sku?.id as string}
							attributes={selection?.sku?.attributes}
							showDescription
							partNumber={selection?.sku?.partNumber}
						/>
					</PaperCard>
					<DeliveryMethodSelector product={selection.sku ?? product} />
					<PaperCard sx={kitSX.paperPrice}>
						<ProductDetailsAddToCart
							isIconButton
							showQuantityInput
							showAvailabilityLabel
							showMinimumQtyLabel
						/>
					</PaperCard>
					<EMarketingSpot
						id={KDP_UX_COMPONENT_ESPOT}
						properties={{ emsName: KDP_UX_COMPONENT_ESPOT }}
					/>
					<ProductAbout isKit />
					<PaperCard sx={productDetailsContainerSX}>
						<Typography variant="headingSubtitle" sx={kitSX.paperTitle}>
							{KitTittle.t()}
						</Typography>
						<KitTable />
					</PaperCard>
					<ProductAvailability />
					<ProductDetailsAttributes />
					<EMarketingSpot
						id={KDP_CROSS_SELL_ESPOT}
						properties={{ emsName: KDP_CROSS_SELL_ESPOT }}
					/>
				</Box>
			</Box>
		</ContentProvider>
	);
};
